import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  LogIn,
  Mail,
  Calendar,
  FileText,
  DollarSign,
  Building2,
  UserCircle,
  Users,
  Package,
  AlertCircle,
  RefreshCw,
} from 'lucide-react'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'
import { useAdminUserDetail, useImpersonateUser } from '../hooks/useAdmin'
import { useAdminStore } from '../stores/adminStore'
import { toast } from '../hooks/useToast'
import { getApiError } from '../lib/api'

export default function AdminUserDetail() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const userId = Number(id)

  const { data: user, isLoading, error, refetch } = useAdminUserDetail(userId)
  const impersonateMutation = useImpersonateUser()
  const startImpersonation = useAdminStore((s) => s.startImpersonation)

  const handleImpersonate = async () => {
    if (!user) return
    try {
      const result = await impersonateMutation.mutateAsync(user.id)
      startImpersonation(result.user, result.token)
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardHeader>
            </Card>
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (error) {
    const apiError = getApiError(error)
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h3 className="text-lg font-semibold">{t('admin.users.detail.errorLoading')}</h3>
        <p className="text-muted-foreground mb-4">{apiError.message}</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/dashboard/admin/users')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common.back')}
          </Button>
          <Button onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('common.retry')}
          </Button>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <h3 className="text-lg font-semibold">{t('admin.users.detail.notFound')}</h3>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate('/dashboard/admin/users')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('common.back')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard/admin/users')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight">{user.name}</h1>
              <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                {t(`admin.users.roles.${user.role}`)}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">{user.email}</p>
          </div>
        </div>
        {user.role !== 'admin' && (
          <Button onClick={handleImpersonate} disabled={impersonateMutation.isPending}>
            <LogIn className="mr-2 h-4 w-4" />
            {t('admin.users.openCabinet')}
          </Button>
        )}
      </div>

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle>{t('admin.users.detail.info')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t('admin.users.table.email')}:</span>
              <span>{user.email}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">{t('admin.users.table.registered')}:</span>
              <span>{formatDate(user.createdAt)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <p className="text-sm text-muted-foreground">{t('admin.users.detail.invoices')}</p>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{user.invoiceCount}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <p className="text-sm text-muted-foreground">{t('admin.users.detail.totalInvoiced')}</p>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{formatCurrency(user.totalInvoiced)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <p className="text-sm text-muted-foreground">{t('admin.users.detail.profiles')}</p>
            <UserCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{user.senderProfiles.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <p className="text-sm text-muted-foreground">{t('admin.users.detail.accounts')}</p>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{user.bankAccounts.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="bank-accounts">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="bank-accounts">
            <Building2 className="mr-2 h-4 w-4" />
            {t('admin.users.detail.tabs.bankAccounts')}
          </TabsTrigger>
          <TabsTrigger value="sender-profiles">
            <UserCircle className="mr-2 h-4 w-4" />
            {t('admin.users.detail.tabs.senderProfiles')}
          </TabsTrigger>
          <TabsTrigger value="clients">
            <Users className="mr-2 h-4 w-4" />
            {t('admin.users.detail.tabs.clients')}
          </TabsTrigger>
          <TabsTrigger value="products">
            <Package className="mr-2 h-4 w-4" />
            {t('admin.users.detail.tabs.products')}
          </TabsTrigger>
        </TabsList>

        {/* Bank Accounts */}
        <TabsContent value="bank-accounts">
          <Card>
            <CardContent className="p-0">
              {user.bankAccounts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Building2 className="h-8 w-8 text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">{t('admin.users.detail.noData')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('settings.bankAccounts.name')}</TableHead>
                      <TableHead>{t('settings.bankAccounts.bankName')}</TableHead>
                      <TableHead className="hidden md:table-cell">{t('settings.bankAccounts.accountNumber')}</TableHead>
                      <TableHead className="hidden lg:table-cell">{t('settings.bankAccounts.iban')}</TableHead>
                      <TableHead>{t('settings.bankAccounts.defaultSet')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {user.bankAccounts.map((account) => (
                      <TableRow key={account.id}>
                        <TableCell className="font-medium">{account.name}</TableCell>
                        <TableCell>{account.bankName}</TableCell>
                        <TableCell className="hidden md:table-cell">{account.accountNumber}</TableCell>
                        <TableCell className="hidden lg:table-cell">{account.iban || '—'}</TableCell>
                        <TableCell>
                          {account.isDefault && (
                            <Badge variant="secondary">{t('settings.bankAccounts.defaultSet')}</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sender Profiles */}
        <TabsContent value="sender-profiles">
          <Card>
            <CardContent className="p-0">
              {user.senderProfiles.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <UserCircle className="h-8 w-8 text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">{t('admin.users.detail.noData')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('settings.senderProfiles.profileName')}</TableHead>
                      <TableHead>{t('settings.senderProfiles.companyName')}</TableHead>
                      <TableHead className="hidden md:table-cell">{t('settings.senderProfiles.taxId')}</TableHead>
                      <TableHead className="hidden lg:table-cell">{t('settings.senderProfiles.email')}</TableHead>
                      <TableHead>{t('settings.bankAccounts.default')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {user.senderProfiles.map((profile) => (
                      <TableRow key={profile.id}>
                        <TableCell className="font-medium">{profile.name}</TableCell>
                        <TableCell>{profile.companyName}</TableCell>
                        <TableCell className="hidden md:table-cell">{profile.taxId || '—'}</TableCell>
                        <TableCell className="hidden lg:table-cell">{profile.email || '—'}</TableCell>
                        <TableCell>
                          {profile.isDefault && (
                            <Badge variant="secondary">{t('settings.bankAccounts.defaultSet')}</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Clients */}
        <TabsContent value="clients">
          <Card>
            <CardContent className="p-0">
              {user.clients.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Users className="h-8 w-8 text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">{t('admin.users.detail.noData')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('settings.clients.name')}</TableHead>
                      <TableHead className="hidden md:table-cell">{t('settings.clients.taxId')}</TableHead>
                      <TableHead>{t('settings.clients.email')}</TableHead>
                      <TableHead className="hidden lg:table-cell">{t('settings.clients.city')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {user.clients.map((client) => (
                      <TableRow key={client.id}>
                        <TableCell className="font-medium">{client.name}</TableCell>
                        <TableCell className="hidden md:table-cell">{client.taxId || '—'}</TableCell>
                        <TableCell>{client.email || '—'}</TableCell>
                        <TableCell className="hidden lg:table-cell">{client.city || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Products */}
        <TabsContent value="products">
          <Card>
            <CardContent className="p-0">
              {user.products.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Package className="h-8 w-8 text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">{t('admin.users.detail.noData')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('settings.products.name')}</TableHead>
                      <TableHead>{t('settings.products.price')}</TableHead>
                      <TableHead className="hidden md:table-cell">{t('settings.products.vatRate')}</TableHead>
                      <TableHead className="hidden lg:table-cell">{t('settings.products.unit')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {user.products.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell className="font-medium">{product.name}</TableCell>
                        <TableCell>{formatCurrency(product.price)}</TableCell>
                        <TableCell className="hidden md:table-cell">{product.vatRate}%</TableCell>
                        <TableCell className="hidden lg:table-cell">{product.unit || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
